import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';
import type { StreamStats } from '@/types';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/** Generate a nanoid-style random room ID without the npm dep */
export function generateRoomId(): string {
  const chars = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
  const arr = new Uint8Array(12);
  crypto.getRandomValues(arr);
  return Array.from(arr)
    .map((b) => chars[b % chars.length])
    .join('');
}

export function generateParticipantId(): string {
  return `p_${generateRoomId()}`;
}

/** Format seconds as HH:MM:SS or MM:SS */
export function formatTime(seconds: number): string {
  if (!isFinite(seconds) || isNaN(seconds)) return '0:00';
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);
  if (h > 0) {
    return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  }
  return `${m}:${String(s).padStart(2, '0')}`;
}

/** Format bytes as human-readable string */
export function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
}

/** Clamp a number between min and max */
export function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

/** Debounce a function */
export function debounce<T extends (...args: any[]) => void>(
  fn: T,
  delay: number
): (...args: Parameters<T>) => void {
  let timer: ReturnType<typeof setTimeout>;
  return (...args: Parameters<T>) => {
    clearTimeout(timer);
    timer = setTimeout(() => fn(...args), delay);
  };
}

/** Determine quality label from stats */
export function computeQualityLabel(stats: Partial<StreamStats>): StreamStats['qualityLabel'] {
  const { bitrate = 0, packetLoss = 0, roundTripTime = 0 } = stats;
  if (packetLoss > 0.1 || roundTripTime > 500 || bitrate < 500) return 'poor';
  if (packetLoss > 0.05 || roundTripTime > 200 || bitrate < 1500) return 'fair';
  if (packetLoss > 0.02 || roundTripTime > 100 || bitrate < 3000) return 'good';
  return 'excellent';
}

/** Parse SRT text into VTT-compatible cues */
export function parseSRT(srt: string): string {
  const lines = srt.replace(/\r\n/g, '\n').replace(/\r/g, '\n').trim().split('\n');
  let vtt = 'WEBVTT\n\n';
  let i = 0;
  while (i < lines.length) {
    // skip index line
    if (/^\d+$/.test(lines[i]?.trim() ?? '')) i++;
    // timing line
    const timingLine = lines[i]?.trim() ?? '';
    const timingMatch = timingLine.match(
      /(\d{2}:\d{2}:\d{2},\d{3})\s*-->\s*(\d{2}:\d{2}:\d{2},\d{3})/
    );
    if (timingMatch) {
      const start = timingMatch[1].replace(',', '.');
      const end = timingMatch[2].replace(',', '.');
      vtt += `${start} --> ${end}\n`;
      i++;
      // text lines
      while (i < lines.length && lines[i]?.trim() !== '') {
        vtt += `${lines[i]}\n`;
        i++;
      }
      vtt += '\n';
    } else {
      i++;
    }
  }
  return vtt;
}
